import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Resolve, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { InventoryControllerService, ProductDto } from '../@api';
import {MOCK_PRODUCTS} from '../../assets/data/products.mock';

@Injectable({
    providedIn: 'root',
})
export class ProductResolverService implements Resolve<ProductDto> {
    constructor(
        private inventoryService: InventoryControllerService,
        private http: HttpClient,
        @Inject(PLATFORM_ID) private platformId: Object
    ) {}

    async resolve(route: ActivatedRouteSnapshot,
                  state: RouterStateSnapshot): Promise<ProductDto> {
        const productCode = route.data['productCode'];

        if (!productCode) {
            console.error('Product code is missing in route data');
            return this.getDefaultProduct(productCode);
        }

        if (isPlatformServer(this.platformId)) {
            // Use imported mock data for server-side pre-rendering
            return this.getMockProduct(productCode);
        } else {
            // Fetch real data on client-side
            try {
                return await lastValueFrom(this.inventoryService.findProductByCode(productCode));
            } catch (error) {
                console.error(`Error fetching product ${productCode} from API:`, error);
                return this.getMockProduct(productCode);
            }
        }
    }

    private getMockProduct(productCode: string): Promise<ProductDto> {
        // Use imported MOCK_PRODUCTS instead of HTTP request
        try {
            const product = MOCK_PRODUCTS.find((p: ProductDto): boolean => p.code === productCode);
            if (product) {
                console.log(`Found mock product for ${productCode}:`, product);
                return Promise.resolve(product);
            } else {
                console.warn(`No mock product found for ${productCode} in mock data`);
                return Promise.resolve(this.getDefaultProduct(productCode));
            }
        } catch (error) {
            console.error(`Error processing mock products for ${productCode}:`, error);
            return Promise.resolve(this.getDefaultProduct(productCode));
        }
    }

    private getDefaultProduct(productCode: string): ProductDto {
        console.warn(`Returning default product for ${productCode}`);
        return {
            name: 'Default Product',
            title: 'Product | Solar Central',
            description: 'Explore solar products at Solar Central.',
            keywords: 'solar, product, solar central',
            image: 'https://www.solarcentral.co.za/images/logo.png',
            images: [],
            code: productCode || 'unknown',
            category: 'unknown'
        };
    }
}